import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { NetworkConfig } from '../config/networks';
import { useWallet } from './WalletContext';
import { useAuth } from './AuthContext';
import { listPolicies, uiPolicyTypeFromApi } from '../services/trustline';

export interface NamedPolicy {
  id: string;
  name: string;
  policyType: string;
  customizations: {
    /** Initiator allowlist → allowedEmailDomains */
    emails?: string[];
    /** Validator allowlist → allowedAdditionalAuthenticationEmailDomains */
    validatorEmails?: string[];
    /** How many distinct validators must approve */
    requiredAdditionalAuthentications?: number;
  };
  /** Loaded from listPolicies — already persisted server-side */
  isExisting?: boolean;
  apiType?: string;
  apiConfig?: Record<string, any>;
}

export interface MethodMapping {
  methodName: string;
  policyId: string | null;
}

export interface WizardData {
  email: string;
  dAppName: string;
  chain: NetworkConfig | null;
  contractAddress: string;
  validationEngineProxyAddress: string | null;
  validationEngineImplementationAddress: string | null;
  isTrustlineFirewall: boolean;
  firewallTargetAddress: string | null;
  contractABI: string;
  namedPolicies: NamedPolicy[];
  methodMappings: MethodMapping[];
  sanctionsEnabled: boolean | null;
  sanctionsOracleKey: string | null;
  onChainConfigSynced: boolean;
  isSubmitting: boolean;
  submissionError: string | null;
  submissionSuccess: boolean;
  /** Set after successful onboarding for redirect */
  submittedChainId: string | null;
  submittedContractAddress: string | null;
}

interface WizardContextType {
  currentStep: number;
  totalSteps: number;
  wizardData: WizardData;
  setCurrentStep: (step: number) => void;
  updateWizardData: (data: Partial<WizardData>) => void;
  resetWizard: () => void;
  canGoNext: () => boolean;
  canGoBack: () => boolean;
  nextStep: () => void;
  prevStep: () => void;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

const initialWizardData: WizardData = {
  email: '',
  dAppName: '',
  chain: null,
  contractAddress: '',
  validationEngineProxyAddress: null,
  validationEngineImplementationAddress: null,
  isTrustlineFirewall: false,
  firewallTargetAddress: null,
  contractABI: '',
  namedPolicies: [],
  methodMappings: [],
  sanctionsEnabled: null,
  sanctionsOracleKey: null,
  onChainConfigSynced: false,
  isSubmitting: false,
  submissionError: null,
  submissionSuccess: false,
  submittedChainId: null,
  submittedContractAddress: null,
};

interface WizardProviderProps {
  children: ReactNode;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({ children }) => {
  const { email } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState<WizardData>({
    ...initialWizardData,
    email,
  });
  const totalSteps = 8;
  const { isConnected } = useWallet();

  useEffect(() => {
    setWizardData((prev) => ({ ...prev, email }));
  }, [email]);

  // Prefetch existing client policies for the mapping step
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await listPolicies();
      if (cancelled || !res.success || !res.data?.policies) return;
      const existing: NamedPolicy[] = res.data.policies.map((p) => ({
        id: `existing-${p.policyId}`,
        name: p.name,
        policyType: uiPolicyTypeFromApi(p.type),
        customizations: {
          emails: Array.isArray(p.config?.allowedEmailDomains)
            ? p.config.allowedEmailDomains
            : undefined,
          validatorEmails: Array.isArray(p.config?.allowedAdditionalAuthenticationEmailDomains)
            ? p.config.allowedAdditionalAuthenticationEmailDomains
            : undefined,
          requiredAdditionalAuthentications:
            typeof p.config?.requiredAdditionalAuthentications === 'number'
              ? p.config.requiredAdditionalAuthentications
              : undefined,
        },
        isExisting: true,
        apiType: p.type,
        apiConfig: p.config || {},
      }));
      setWizardData((prev) => {
        const localOnly = prev.namedPolicies.filter((p) => !p.isExisting);
        const existingIds = new Set(existing.map((p) => p.id));
        const keptLocal = localOnly.filter(
          (p) => !existing.some((e) => e.name === p.name)
        );
        return {
          ...prev,
          namedPolicies: [...existing, ...keptLocal.filter((p) => !existingIds.has(p.id))],
        };
      });
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateWizardData = useCallback((data: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...data }));
  }, []);

  const resetWizard = useCallback(() => {
    setCurrentStep(1);
    setWizardData({ ...initialWizardData, email });
  }, [email]);

  // If wallet disconnects mid-flow, return to step 1 but keep policies/auth
  useEffect(() => {
    if (!isConnected && currentStep > 1) {
      setCurrentStep(1);
    }
  }, [isConnected, currentStep]);

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 1:
        return isConnected;
      case 2:
        return !!(
          wizardData.dAppName.trim() &&
          wizardData.chain &&
          wizardData.contractAddress.trim()
        );
      case 3:
        return !!wizardData.validationEngineProxyAddress;
      case 4:
        return !!wizardData.contractABI.trim();
      case 5:
        return true;
      case 6: {
        const sanctionsOk =
          wizardData.sanctionsEnabled !== null &&
          (wizardData.sanctionsEnabled === false ||
            (wizardData.sanctionsOracleKey != null &&
              wizardData.sanctionsOracleKey.trim() !== ''));
        return sanctionsOk && wizardData.onChainConfigSynced;
      }
      case 7:
        return true;
      case 8:
        return false;
      default:
        return false;
    }
  };

  const canGoBack = (): boolean => currentStep > 1;

  const nextStep = () => {
    if (canGoNext() && currentStep < totalSteps) {
      setCurrentStep((prev) => (prev + 1 <= totalSteps ? prev + 1 : prev));
    }
  };

  const prevStep = () => {
    if (canGoBack()) {
      setCurrentStep((prev) => (prev - 1 >= 1 ? prev - 1 : prev));
    }
  };

  return (
    <WizardContext.Provider
      value={{
        currentStep,
        totalSteps,
        wizardData,
        setCurrentStep,
        updateWizardData,
        resetWizard,
        canGoNext,
        canGoBack,
        nextStep,
        prevStep,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};
